import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Wordmark } from "../components/Wordmark";
import { toast } from "sonner";
import { Loader2, Check, LogOut, Clock, Sparkles } from "lucide-react";
import { useCurrency, LOCAL } from "../lib/region";

export default function Subscribe() {
  const navigate = useNavigate();
  const cur = useCurrency();
  const { user, logout, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const [promo, setPromo] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  const redeemPromo = async () => {
    if (!promo.trim()) { toast.error("Please enter a code."); return; }
    setPromoBusy(true);
    try {
      await api.post("/promo/redeem", { code: promo.trim() });
      await refreshUser();
      toast.success("Code applied — welcome back!");
      navigate("/home", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "That code isn't valid.");
      setPromoBusy(false);
    }
  };

  useEffect(() => {
    if (user?.access?.gate === "ok") navigate("/home", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => { api.get("/payments/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Couldn't start checkout. Please try again."); setBusy(""); }
  };

  return (
    <div className="min-h-screen grain bg-cream flex flex-col" data-testid="subscribe-page">
      <header className="border-b border-line bg-cream/85 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" />
            <Wordmark size="text-lg sm:text-xl" />
          </div>
          <button data-testid="subscribe-logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 text-subtle hover:text-charcoal text-sm">
            <LogOut size={15} /> Log out
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-12 relative z-10">
        <div className="text-center max-w-xl mx-auto">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-coral font-semibold">
            <Clock size={14} /> Your free trial has ended
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mt-3 leading-tight">
            Choose a plan to keep writing your story
          </h1>
          <p className="text-subtle mt-3 leading-relaxed">
            Your memories are safe and waiting. Pick a plan to unlock the app again — every memory,
            chapter and photo you've added is exactly where you left it.
          </p>
          <span data-testid="limited-offer-badge" className="mt-4 inline-flex items-center gap-2 bg-coral/10 text-coral font-semibold text-sm px-4 py-1.5 rounded-full">
            <Sparkles size={15} /> Limited-time launch offer — lock in these prices while they last
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 mt-10 max-w-2xl mx-auto">
          {plans.map((p) => (
            <div key={p.lookup_key} data-testid={`subscribe-plan-${p.lookup_key}`}
              className="bg-white border border-line rounded-2xl p-6 flex flex-col hover:border-sage transition-colors animate-fade-up">
              <p className="font-serif text-xl text-charcoal">{p.name}</p>
              <p className="mt-1"><span className="font-serif text-3xl text-teal">{p.price}</span> <span className="text-subtle text-sm">{p.cadence}</span>{cur !== "GBP" && <span className="block text-subtle text-xs mt-1">≈ {cur === "USD" ? p.price_usd : p.price_cad} {p.cadence} for {LOCAL[cur].who} customers</span>}</p>
              <p className="text-subtle text-sm mt-3 flex-1">{p.blurb}</p>
              <button data-testid={`subscribe-btn-${p.lookup_key}`} onClick={() => subscribe(p.lookup_key)} disabled={!!busy}
                className="mt-5 inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-4 h-12 rounded-full font-semibold text-sm disabled:opacity-60">
                {busy === p.lookup_key ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                {p.recurring ? "Subscribe" : "Buy lifetime"}
              </button>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-subtle mt-6" data-testid="subscribe-stripe-note">Payments are collected by Stripe, which automatically converts currencies depending on your location.</p>

        <div className="max-w-md mx-auto mt-8 bg-white border border-line rounded-2xl p-5 text-center">
          <p className="text-sm text-charcoal font-medium">Have an invite code?</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value)}
              placeholder="Enter your code" onKeyDown={(e) => e.key === "Enter" && redeemPromo()}
              className="flex-1 h-11 px-4 rounded-full border border-line bg-white uppercase text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/20" />
            <button data-testid="promo-redeem-btn" onClick={redeemPromo} disabled={promoBusy}
              className="bg-teal hover:bg-tealdark text-cream px-5 h-11 rounded-full font-semibold text-sm disabled:opacity-60">
              {promoBusy ? "Applying…" : "Apply"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
